import Phaser from 'phaser'

export interface ProgressDisplayConfig {
  x: number
  y: number
  total: number
  label?: string
}

/**
 * Reusable progress display (e.g., "3/10 strawberries")
 */
export class ProgressDisplay extends Phaser.GameObjects.Container {
  private progressText: Phaser.GameObjects.Text
  private current: number = 0
  private total: number

  constructor(scene: Phaser.Scene, config: ProgressDisplayConfig) {
    super(scene, config.x, config.y)

    const { total, label = '' } = config
    this.total = total

    // Background
    const bg = scene.add.rectangle(0, 0, 140, 50, 0x000000, 0.5)
    bg.setStrokeStyle(2, 0xffffff, 0.3)

    // Label (if provided)
    if (label) {
      const labelText = scene.add.text(-60, -15, label, {
        fontFamily: 'system-ui',
        fontSize: '12px',
        color: '#ffffff',
      })
      labelText.setOrigin(0, 0.5)
      labelText.setAlpha(0.8)
      this.add(labelText)
    }

    // Progress value
    this.progressText = scene.add.text(0, label ? 5 : -8, `0/${total}`, {
      fontFamily: 'system-ui',
      fontSize: '24px',
      fontStyle: 'bold',
      color: '#ffffff',
    })
    this.progressText.setOrigin(0.5, 0.5)

    this.add([bg, this.progressText])
    scene.add.existing(this)
  }

  /**
   * Set current progress
   */
  setProgress(current: number, animate: boolean = true): void {
    this.current = Math.min(current, this.total)
    this.progressText.setText(`${this.current}/${this.total}`)

    if (animate) {
      this.scene.tweens.add({
        targets: this.progressText,
        scale: 1.2,
        duration: 100,
        yoyo: true,
        ease: 'Quad.easeOut',
      })
    }

    // Highlight when complete
    if (this.current >= this.total) {
      this.progressText.setColor('#4caf50')
    }
  }

  /**
   * Increment progress by 1
   */
  increment(animate: boolean = true): number {
    this.setProgress(this.current + 1, animate)
    return this.current
  }

  /**
   * Get current progress
   */
  getCurrent(): number {
    return this.current
  }

  /**
   * Check if progress is complete
   */
  isComplete(): boolean {
    return this.current >= this.total
  }
}
